import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import {
  LayoutDashboard,
  Settings,
  LogOut,
  Shield,
  UserCog,
  Menu,
  X,
  Search,
  Sun,
  Moon,
  KeyRound,
  CalendarDays,
  IdCard,
  NotebookPen,
  FileText,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { getUnreadNotificationCount } from "../services/api";
import { TABS, ROLE_LABELS, ROLE_COLORS } from "../utils/tabs";
import AIAssistant from "./AIAssistant";
import { getMyProfile } from "../services/api";

const UserAvatar = ({ user }) => {
  const [photo, setPhoto] = useState(null);
  useEffect(() => {
    setPhoto(null);
    if (!user?.employee_id) return;
    let active = true;
    getMyProfile().then((r) => { if (active) setPhoto(r.data.photo || null); }).catch(() => {});
    return () => { active = false; };
  }, [user?.employee_id]);
  const initials = (user?.name || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  return (
    <span className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#26788A] to-[#1D5E6C] text-white text-xs font-bold ring-2 ring-[#26788A]/20" title={user?.name} data-testid="user-avatar">
      {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : initials}
    </span>
  );
};
import GlobalSearch from "./GlobalSearch";
import ChangeMyPasswordDialog from "./ChangeMyPasswordDialog";

const Sidebar = ({ isOpen, onClose, onSearchOpen }) => {
  const { user, logout, hasPermission, canAccessTab } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [pwdOpen, setPwdOpen] = useState(false);

  useEffect(() => {
    onClose?.();
  }, [location.pathname]);

  const baseNavItems = [
    { to: "/", icon: LayoutDashboard, label: "Tableau de bord", tab: null },
    { to: "/mon-planning", icon: CalendarDays, label: "Mon planning", tab: null },
    { to: "/mes-infos", icon: IdCard, label: "Mes infos", tab: null },
    ...(user?.role !== "admin" ? [{ to: "/mes-bulletins", icon: FileText, label: "Bulletins de salaire", tab: null }] : []),
    ...(!canAccessTab("tasks") ? [{ to: "/taches/recap", icon: NotebookPen, label: "Récap du jour", tab: null }] : []),
    ...TABS.map((t) => ({ to: t.to, icon: t.icon, label: t.label, tab: t.key, showBadge: t.key === "notifications" })),
  ];

  const adminNavItems = [
    { to: "/bulletins-salaire", icon: FileText, label: "Bulletins de salaire", permission: "manage_users" },
    { to: "/utilisateurs", icon: UserCog, label: "Utilisateurs", permission: "manage_users" },
    { to: "/reglages", icon: Settings, label: "Réglages", permission: "view_settings" },
  ];

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const res = await getUnreadNotificationCount();
        setUnreadCount(res.data.unread_count);
      } catch (err) {
        // Ignore errors
      }
    };
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    window.addEventListener("notif-refresh", loadUnreadCount);
    return () => { clearInterval(interval); window.removeEventListener("notif-refresh", loadUnreadCount); };
  }, []);

  const navItems = [
    ...baseNavItems.filter(item => !item.tab || canAccessTab(item.tab)),
    ...adminNavItems.filter(item => hasPermission(item.permission))
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    onClose?.();
    logout();
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside data-testid="app-sidebar" className={`
        fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
        w-64 lg:w-60 h-dvh max-h-dvh min-h-0 overflow-hidden shrink-0 bg-white border-r border-slate-200
        flex flex-col transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo + avatar */}
        <div className="p-5 pb-4 shrink-0 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <UserAvatar user={user} />
            <img
              src="/logo-studio-raneem.png"
              alt="Studio Raneem"
              className="h-9 w-auto brightness-0 dark:invert"
            />
          </div>
          <button
            onClick={onClose}
            data-testid="mobile-menu-close-btn"
            aria-label="Fermer le menu"
            className="lg:hidden p-2 rounded-md hover:bg-slate-100"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Recherche globale */}
        <div className="px-3 pb-3 shrink-0">
          <button
            onClick={onSearchOpen}
            data-testid="global-search-trigger"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-400 hover:border-slate-300 hover:text-slate-600 transition-colors text-sm"
          >
            <Search size={15} />
            <span className="flex-1 text-left">Rechercher…</span>
            <kbd className="text-[10px] font-mono bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded text-slate-500">⌘K</kbd>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 px-3 space-y-0.5 overflow-y-auto overscroll-contain border-t border-slate-100 pt-3" data-testid="sidebar-navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-').replace(/&/g, '')}`}
              className={`sidebar-link !min-h-11 lg:!min-h-0 !py-2 !px-3 ${isActive(item.to) ? 'sidebar-link-active' : ''}`}
            >
              <item.icon size={17} strokeWidth={isActive(item.to) ? 2.2 : 1.8} />
              <span className="flex-1 text-sm">{item.label}</span>
              {item.showBadge && unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="p-3 shrink-0 border-t border-slate-200" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom, 0px))" }}>
          <div className="flex items-center gap-3 px-3 py-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-[#26788A] flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-white">
                {user?.name?.charAt(0).toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={() => setPwdOpen(true)}
              data-testid="my-password-btn"
              title="Changer mon mot de passe"
              className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            >
              <KeyRound size={16} />
            </button>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              title={theme === "dark" ? "Mode clair" : "Mode sombre"}
              className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
          <div className="px-3 mb-2">
            <Badge className={`text-xs ${ROLE_COLORS[user?.role] || 'bg-slate-100 text-slate-700'}`}>
              <Shield size={10} className="mr-1" />
              {ROLE_LABELS[user?.role] || user?.role}
            </Badge>
          </div>
          <Button
            variant="ghost"
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full justify-start text-slate-500 hover:text-slate-900 hover:bg-slate-100"
          >
            <LogOut size={16} className="mr-2" />
            Déconnexion
          </Button>
        </div>
        <ChangeMyPasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      </aside>
    </>
  );
};

const MobileHeader = ({ onMenuClick, onSearchOpen }) => {
  return (
    <header
      className="lg:hidden sticky top-0 z-30 shrink-0 bg-white border-b border-slate-200 px-4 pb-3 flex items-center gap-3"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      data-testid="mobile-header"
    >
      <button
        onClick={onMenuClick}
        className="min-h-11 min-w-11 flex items-center justify-center rounded-md hover:bg-slate-100 -ml-2"
        aria-label="Ouvrir le menu"
        data-testid="mobile-menu-btn"
      >
        <Menu size={24} className="text-slate-700" />
      </button>
      <img
        src="/logo-studio-raneem.png"
        alt="Studio Raneem"
        className="h-8 w-auto brightness-0 dark:invert"
      />
      <button
        onClick={onSearchOpen}
        className="ml-auto min-h-11 min-w-11 flex items-center justify-center rounded-md hover:bg-slate-100"
        aria-label="Rechercher"
        data-testid="mobile-search-btn"
      >
        <Search size={20} className="text-slate-500" />
      </button>
    </header>
  );
};

const Layout = ({ children }) => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row min-h-dvh bg-slate-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onSearchOpen={() => setSearchOpen(true)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} onSearchOpen={() => setSearchOpen(true)} />
      <main className="flex-1 min-w-0" data-testid="app-main">
        {children}
      </main>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* AI Assistant - Always accessible */}
      {user?.role === "admin" && <AIAssistant />}
    </div>
  );
};

export default Layout;
